import React, { useContext, useRef, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { EditorContext } from '../../../../utils/contexts';
import CollapsedPanel from '../../components/CollapsedPanel';
import { FORM_FIELD_CATALOGUE } from '../../forms/constants';

// ─── Design Tokens ────────────────────────────────────────────────────────────
const BLUE       = '#2563EB';
const BLUE_ALPHA = 'rgba(37,99,235,0.10)';
const GRAY_50    = '#F8FAFC';
const GRAY_100   = '#F1F5F9';
const GRAY_200   = '#E2E8F0';
const GRAY_300   = '#CBD5E1';
const GRAY_400   = '#94A3B8';
const GRAY_500   = '#64748B';
const GRAY_700   = '#334155';
const GRAY_900   = '#0F172A';
const RED        = '#EF4444';
const RED_BG     = '#FEF2F2';

const ALL_FIELDS  = FORM_FIELD_CATALOGUE.flatMap(c => c.fields);
const getFieldDef = (type) => ALL_FIELDS.find(f => f.type === type) || { label: type, icon: '?' };
const typeLabel   = (type) => getFieldDef(type).label;
const typeIcon    = (type) => getFieldDef(type).icon;


// ─── Layout ───────────────────────────────────────────────────────────────────
const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif;
`;

const Section = styled.div`
  border-bottom: 1px solid ${GRAY_200};
  &:last-child { border-bottom: none; }
`;

const SectionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  &:hover { background: ${GRAY_50}; }
`;

const SectionTitle = styled.span`
  font-size: 0.78rem;
  font-weight: 700;
  color: ${GRAY_700};
  letter-spacing: 0.01em;
`;

const SectionChevron = styled.span`
  font-size: 0.7rem;
  color: ${GRAY_400};
  transition: transform 0.2s;
  transform: ${p => p.$open ? 'rotate(180deg)' : 'none'};
`;

const SectionBody = styled.div`
  padding: 0 0 8px;

`;

// ─── Search ───────────────────────────────────────────────────────────────────
const SearchWrap = styled.div`
  position: relative;
  padding: 10px 16px 6px;
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 26px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.8rem;
  color: ${GRAY_400};
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  height: 36px;
  padding: 0 10px 0 32px;
  border: 1.5px solid ${GRAY_200};
  border-radius: 8px;
  font-size: 0.8rem;
  color: ${GRAY_700};
  background: ${GRAY_50};
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  &::placeholder { color: ${GRAY_400}; }
  &:focus {
    border-color: ${BLUE};
    box-shadow: 0 0 0 3px ${BLUE_ALPHA};
    background: #fff;
  }
`;

// ─── Field Library Rows ───────────────────────────────────────────────────────
const CatLabel = styled.div`
  font-size: 0.65rem;
  font-weight: 700;
  color: ${GRAY_400};
  letter-spacing: 0.08em;
  padding: 10px 16px 4px;
  text-transform: uppercase;
`;

const LibraryRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  height: 40px;
  padding: 0 16px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;
  &:hover { background: ${GRAY_50}; }
  &:active { background: ${GRAY_100}; }
`;

const LibIcon = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: ${GRAY_100};
  color: ${GRAY_500};
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const LibLabel = styled.span`
  font-size: 0.82rem;
  color: ${GRAY_700};
  font-weight: 500;
  flex: 1;
`;

const LibAdd = styled.span`
  font-size: 0.75rem;
  color: ${GRAY_400};
  opacity: 0;
  transition: opacity 0.12s;
  ${LibraryRow}:hover & { opacity: 1; }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 20px 16px;
  font-size: 0.8rem;
  color: ${GRAY_400};
`;

// ─── Structure ────────────────────────────────────────────────────────────────
const StructureWrap = styled.div`
  padding: 4px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 28px 16px;
  margin: 4px 12px;
  border: 2px dashed ${GRAY_200};
  border-radius: 12px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: 1.5rem;
  opacity: 0.4;
`;

const EmptyText = styled.div`
  font-size: 0.8rem;
  color: ${GRAY_400};
  line-height: 1.5;
`;

// ─── Field Card ───────────────────────────────────────────────────────────────
const FieldCard = styled.div`
  border-radius: 10px;
  border: 1.5px solid ${p => p.$selected ? BLUE : GRAY_200};
  box-shadow: ${p => p.$selected ? `0 0 0 3px ${BLUE_ALPHA}` : 'none'};
  background: #fff;
  opacity: ${p => p.$dragging ? 0.4 : 1};
  transition: border-color 0.15s, box-shadow 0.15s;
  overflow: hidden;
`;

const FieldCardRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 10px 10px 8px;
  cursor: pointer;
  user-select: none;
  &:hover { background: ${GRAY_50}; }
`;

const DragHandle = styled.span`
  color: ${GRAY_200};
  cursor: grab;
  font-size: 0.85rem;
  flex-shrink: 0;
  padding: 0 2px;
  line-height: 1;
  &:hover { color: ${GRAY_400}; }
  &:active { cursor: grabbing; }
`;

const FieldIconBadge = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: ${GRAY_100};
  color: ${GRAY_500};
  font-size: 0.72rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const FieldInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FieldLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${GRAY_900};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FieldType = styled.div`
  font-size: 0.68rem;
  color: ${GRAY_400};
  margin-top: 1px;
`;

const MenuTrigger = styled.button`
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: none;
  background: none;
  color: ${GRAY_400};
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.12s, color 0.12s;
  &:hover { background: ${GRAY_100}; color: ${GRAY_700}; }
`;

const MenuWrap = styled.div`
  position: relative;
`;

const DropMenu = styled.div`
  position: fixed;
  top: ${p => p.$top}px;
  right: ${p => p.$right}px;
  background: #fff;
  border: 1px solid ${GRAY_200};
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.10);
  min-width: 150px;
  z-index: 9999;
  overflow: hidden;
`;

const MenuItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  background: none;
  border: none;
  font-size: 0.8rem;
  color: ${p => p.$danger ? RED : GRAY_700};
  cursor: pointer;
  text-align: left;
  transition: background 0.1s;
  &:hover { background: ${p => p.$danger ? RED_BG : GRAY_50}; }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: ${GRAY_100};
  margin: 3px 0;
`;

// ─── Inline Editor ────────────────────────────────────────────────────────────
const EditorWrap = styled.div`
  border-top: 1px solid ${GRAY_100};
  background: ${GRAY_50};
`;

const EditorSection = styled.div`
  padding: 0;
`;

const EditorSectionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.68rem;
  font-weight: 700;
  color: ${GRAY_400};
  text-transform: uppercase;
  letter-spacing: 0.07em;
  &:hover { color: ${GRAY_500}; }
`;

const EditorSectionChevron = styled.span`
  font-size: 0.6rem;
  transition: transform 0.18s;
  transform: ${p => p.$open ? 'rotate(180deg)' : 'none'};
`;

const EditorSectionBody = styled.div`
  padding: 0 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;

`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FieldGroupLabel = styled.label`
  font-size: 0.68rem;
  font-weight: 600;
  color: ${GRAY_500};
  letter-spacing: 0.03em;
`;

const FieldInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  height: 36px;
  padding: 0 10px;
  border: 1.5px solid ${GRAY_200};
  border-radius: 8px;
  font-size: 0.8rem;
  color: ${GRAY_900};
  background: #fff;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  font-family: inherit;
  &::placeholder { color: ${GRAY_400}; }
  &:focus { border-color: ${BLUE}; box-shadow: 0 0 0 3px ${BLUE_ALPHA}; }
`;

const FieldSelect = styled.select`
  width: 100%;
  box-sizing: border-box;
  height: 36px;
  padding: 0 10px;
  border: 1.5px solid ${GRAY_200};
  border-radius: 8px;
  font-size: 0.8rem;
  color: ${GRAY_900};
  background: #fff;
  outline: none;
  font-family: inherit;
  cursor: pointer;
  &:focus { border-color: ${BLUE}; box-shadow: 0 0 0 3px ${BLUE_ALPHA}; }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

// ─── Option Editor ────────────────────────────────────────────────────────────
const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1.5px solid ${GRAY_200};
  border-radius: 8px;
  padding: 0 6px 0 4px;
  height: 34px;
  transition: border-color 0.15s;
  &:focus-within { border-color: ${BLUE}; box-shadow: 0 0 0 3px ${BLUE_ALPHA}; }
`;

const OptionDrag = styled.span`
  color: ${GRAY_200};
  font-size: 0.75rem;
  cursor: grab;
  flex-shrink: 0;
  &:hover { color: ${GRAY_400}; }
  &:active { cursor: grabbing; }
`;

const OptionInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.8rem;
  color: ${GRAY_900};
  background: transparent;
  font-family: inherit;
  min-width: 0;
  &::placeholder { color: ${GRAY_400}; }
`;

const OptionDelete = styled.button`
  background: none;
  border: none;
  color: ${GRAY_300};
  cursor: pointer;
  font-size: 0.7rem;
  padding: 2px;
  border-radius: 4px;
  line-height: 1;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: ${GRAY_400};
  &:hover { color: ${RED}; background: ${RED_BG}; }
`;

const AddOptionBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: none;
  border: 1.5px dashed ${GRAY_200};
  border-radius: 8px;
  font-size: 0.78rem;
  color: ${GRAY_400};
  cursor: pointer;
  width: 100%;
  transition: border-color 0.12s, color 0.12s;
  &:hover { border-color: ${BLUE}; color: ${BLUE}; }
`;

// ─── Required Toggle ──────────────────────────────────────────────────────────
const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
`;

const SwitchLabel = styled.span`
  font-size: 0.8rem;
  font-weight: 500;
  color: ${GRAY_700};
`;

const SwitchTrack = styled.button`
  width: 36px;
  height: 20px;
  border-radius: 10px;
  border: none;
  background: ${p => p.$on ? BLUE : GRAY_200};
  cursor: ${p => p.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${p => p.$disabled ? 0.6 : 1};
  position: relative;
  transition: background 0.2s;
  flex-shrink: 0;
`;

const SwitchThumb = styled.span`
  position: absolute;
  top: 2px;
  left: ${p => p.$on ? '18px' : '2px'};
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  transition: left 0.2s;
`;

// ─── Field Editor Component ───────────────────────────────────────────────────
function FieldEditor({ question, onChange, isQuiz, isPoll }) {
  const [openSections, setOpenSections] = useState({ content: true, validation: true, scale: true });

  const update = (field, value) => onChange({ ...question, [field]: value });

  const toggleSection = (key) => setOpenSections(s => ({ ...s, [key]: !s[key] }));

  const showOptions   = ['multiple_choice', 'single_choice', 'dropdown'].includes(question.question_type);
  const showRating    = ['rating', 'rating_scale'].includes(question.question_type);
  const isDisplayOnly = ['heading', 'paragraph'].includes(question.question_type);
  const noPlaceholder = ['rating', 'rating_scale', 'yes_no', 'heading', 'paragraph', 'date', 'file_upload'].includes(question.question_type);

  // Option editor helpers
  const getOptions = () => { try { return JSON.parse(question.options_json || '[]'); } catch { return []; } };
  const setOptions = (opts) => update('options_json', JSON.stringify(opts));

  const optDragRef = useRef(null);

  return (
    <EditorWrap>
      {/* ── Content ── */}
      <EditorSection>
        <EditorSectionHeader onClick={() => toggleSection('content')}>
          Content
          <EditorSectionChevron $open={openSections.content}>▾</EditorSectionChevron>
        </EditorSectionHeader>
        {openSections.content && (
          <EditorSectionBody>
            <FieldGroup>
              <FieldGroupLabel>{isDisplayOnly ? 'Text' : 'Question'}</FieldGroupLabel>
              <FieldInput
                value={question.label || ''}
                onChange={e => update('label', e.target.value)}
                placeholder={isDisplayOnly ? 'Section text…' : 'Enter your question…'}
              />
            </FieldGroup>

            {!noPlaceholder && (
              <FieldGroup>
                <FieldGroupLabel>Placeholder</FieldGroupLabel>
                <FieldInput
                  value={question.placeholder || ''}
                  onChange={e => update('placeholder', e.target.value)}
                  placeholder="Optional hint text"
                />
              </FieldGroup>
            )}

            {showOptions && (
              <FieldGroup>
                <FieldGroupLabel>Options</FieldGroupLabel>
                <OptionList>
                  {getOptions().map((opt, i) => (
                    <OptionRow
                      key={i}
                      draggable
                      onDragStart={() => { optDragRef.current = i; }}
                      onDragOver={e => e.preventDefault()}
                      onDrop={() => {
                        const from = optDragRef.current;
                        if (from === null || from === i) return;
                        const opts = [...getOptions()];
                        const [removed] = opts.splice(from, 1);
                        opts.splice(i, 0, removed);
                        setOptions(opts);
                        optDragRef.current = null;
                      }}
                    >
                      <OptionDrag title="Drag to reorder">⠿</OptionDrag>
                      <OptionInput
                        value={opt}
                        onChange={e => {
                          const opts = [...getOptions()];
                          opts[i] = e.target.value;
                          setOptions(opts);
                        }}
                        placeholder={`Option ${i + 1}`}
                      />
                      <OptionDelete
                        type="button"
                        onClick={() => setOptions(getOptions().filter((_, j) => j !== i))}
                        title="Remove option"
                      >✕</OptionDelete>
                    </OptionRow>
                  ))}
                </OptionList>
                <AddOptionBtn
                  type="button"
                  onClick={() => setOptions([...getOptions(), `Option ${getOptions().length + 1}`])}
                >
                  + Add option
                </AddOptionBtn>
              </FieldGroup>
            )}

            {isQuiz && showOptions && (
              <FieldGroup>
                <FieldGroupLabel>Correct Answer</FieldGroupLabel>
                <FieldSelect
                  value={question.correct_answer || ''}
                  onChange={e => update('correct_answer', e.target.value || null)}
                >
                  <option value="">— None (unscored) —</option>
                  {getOptions().map((opt, i) => (
                    <option key={i} value={opt}>{opt}</option>
                  ))}
                </FieldSelect>
              </FieldGroup>
            )}
          </EditorSectionBody>
        )}
      </EditorSection>

      {/* ── Scale settings ── */}
      {showRating && (
        <EditorSection>
          <EditorSectionHeader onClick={() => toggleSection('scale')}>
            Scale
            <EditorSectionChevron $open={openSections.scale}>▾</EditorSectionChevron>
          </EditorSectionHeader>
          {openSections.scale && (
            <EditorSectionBody>
              <TwoCol>
                <FieldGroup>
                  <FieldGroupLabel>Min</FieldGroupLabel>
                  <FieldInput
                    type="number"
                    min={0}
                    max={10}
                    value={question.scale_min ?? 1}
                    onChange={e => update('scale_min', Math.min(10, Math.max(0, parseInt(e.target.value, 10) || 0)))}
                  />
                </FieldGroup>
                <FieldGroup>
                  <FieldGroupLabel>Max</FieldGroupLabel>
                  <FieldInput
                    type="number"
                    min={0}
                    max={10}
                    value={question.scale_max ?? 5}
                    onChange={e => update('scale_max', Math.min(10, Math.max(0, parseInt(e.target.value, 10) || 0)))}
                  />
                </FieldGroup>
              </TwoCol>
              <TwoCol>
                <FieldGroup>
                  <FieldGroupLabel>Min label</FieldGroupLabel>
                  <FieldInput
                    value={question.scale_min_label || ''}
                    onChange={e => update('scale_min_label', e.target.value)}
                    placeholder="e.g. Poor"
                  />
                </FieldGroup>
                <FieldGroup>
                  <FieldGroupLabel>Max label</FieldGroupLabel>
                  <FieldInput
                    value={question.scale_max_label || ''}
                    onChange={e => update('scale_max_label', e.target.value)}
                    placeholder="e.g. Excellent"
                  />
                </FieldGroup>
              </TwoCol>
            </EditorSectionBody>
          )}
        </EditorSection>
      )}

      {/* ── Validation ── */}
      {!isDisplayOnly && (
        <EditorSection>
          <EditorSectionHeader onClick={() => toggleSection('validation')}>
            Validation
            <EditorSectionChevron $open={openSections.validation}>▾</EditorSectionChevron>
          </EditorSectionHeader>
          {openSections.validation && (
            <EditorSectionBody>
              <SwitchRow>
                <SwitchLabel>Required</SwitchLabel>
                <SwitchTrack
                  $on={isPoll ? true : !!question.required}
                  $disabled={isPoll}
                  onClick={() => { if (!isPoll) update('required', !question.required); }}
                >
                  <SwitchThumb $on={isPoll ? true : !!question.required} />
                </SwitchTrack>
              </SwitchRow>
            </EditorSectionBody>
          )}
        </EditorSection>
      )}
    </EditorWrap>
  );
}

// ─── Field Row Component ──────────────────────────────────────────────────────
function FieldRow({ question, index, total, selected, onSelect, onChange, onRemove, onDuplicate, onMoveUp, onMoveDown, onDragStart, onDragOver, onDrop, onDragEnd, isDragging, isOver, isPoll, isQuiz }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  const label = question.label || `(${typeLabel(question.question_type)})`;
  const icon  = typeIcon(question.question_type);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const openMenu = (e) => {
    e.stopPropagation();
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setMenuOpen(v => !v);
  };

  return (
    <FieldCard
      $selected={selected}
      $dragging={isDragging}
      $isOver={isOver}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      <FieldCardRow onClick={onSelect}>
        <DragHandle
          title="Drag to reorder"
          onClick={e => e.stopPropagation()}
          onMouseDown={e => e.stopPropagation()}
        >⠿</DragHandle>

        <FieldIconBadge>{icon}</FieldIconBadge>

        <FieldInfo>
          <FieldLabel title={label}>{label}</FieldLabel>
          <FieldType>{typeLabel(question.question_type)}</FieldType>
        </FieldInfo>

        {!isPoll && (
          <MenuWrap ref={menuRef}>
            <MenuTrigger
              ref={triggerRef}
              title="More options"
              onClick={openMenu}
            >⋮</MenuTrigger>

            {menuOpen && (
              <DropMenu $top={menuPos.top} $right={menuPos.right} onClick={e => e.stopPropagation()}>
                <MenuItem onClick={() => { onDuplicate(); setMenuOpen(false); }}>
                  ⎘  Duplicate
                </MenuItem>
                <MenuItem onClick={() => { onMoveUp(); setMenuOpen(false); }} disabled={index === 0}>
                  ↑  Move Up
                </MenuItem>
                <MenuItem onClick={() => { onMoveDown(); setMenuOpen(false); }} disabled={index === total - 1}>
                  ↓  Move Down
                </MenuItem>
                <MenuDivider />
                <MenuItem $danger onClick={() => { onRemove(); setMenuOpen(false); }}>
                  ✕  Delete
                </MenuItem>
              </DropMenu>
            )}
          </MenuWrap>
        )}
      </FieldCardRow>

      {selected && (
        <FieldEditor question={question} onChange={onChange} isQuiz={isQuiz} isPoll={isPoll} />
      )}
    </FieldCard>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────
export default function FormBuilderPanel() {
  const { settings, updateSettings } = useContext(EditorContext);

  const [search, setSearch]           = useState('');
  const [libraryOpen, setLibraryOpen] = useState(true);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [overIndex, setOverIndex]     = useState(null);
  const dragIndexRef                  = useRef(null);

  const questions    = settings.form_questions || [];
  const questionsRef = useRef(questions);
  useEffect(() => { questionsRef.current = questions; }, [questions]);

  const setQuestions = useCallback(
    (updated) => updateSettings({ form_questions: updated }),
    [updateSettings]
  );

  // Add field — poll allows only one question, so replace rather than append
  const addField = (fieldDef) => {
    const isPoll = settings.form_type === 'poll';
    const newQ = { question_type: fieldDef.type, label: '', required: isPoll, placeholder: '', ...fieldDef.defaults };
    const next = isPoll ? [newQ] : [...questionsRef.current, newQ];
    setQuestions(next);
    setSelectedIdx(next.length - 1);
    setLibraryOpen(false);
  };

  // Update field
  const updateQuestion = (index, updated) => {
    const next = [...questions];
    next[index] = updated;
    setQuestions(next);
  };

  // Remove field
  const removeQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
    setSelectedIdx(idx => (idx === index ? null : idx > index ? idx - 1 : idx));
  };

  // Duplicate
  const duplicateQuestion = (index) => {
    const next = [...questions];
    next.splice(index + 1, 0, { ...questions[index] });
    setQuestions(next);
    setSelectedIdx(index + 1);
  };

  // Move up / down
  const moveQuestion = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= questions.length) return;
    const next = [...questions];
    [next[index], next[target]] = [next[target], next[index]];
    setQuestions(next);
    setSelectedIdx(target);
  };

  // Drag handlers
  const onDragStart = (index) => (e) => {
    dragIndexRef.current = index;
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (index) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverIndex(index);
  };

  const onDrop = (index) => (e) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    if (from === null || from === index) return;
    const next = [...questions];
    const [removed] = next.splice(from, 1);
    next.splice(index, 0, removed);
    setQuestions(next);
    setSelectedIdx(index);
    dragIndexRef.current = null;
    setOverIndex(null);
  };

  const onDragEnd = () => {
    dragIndexRef.current = null;
    setOverIndex(null);
  };

  // Filtered catalogue for search
  const searchLower = search.toLowerCase();
  const filteredCatalogue = search.trim()
    ? [{ id: 'results', label: 'RESULTS', fields: ALL_FIELDS.filter(f => f.label.toLowerCase().includes(searchLower)) }]
    : FORM_FIELD_CATALOGUE;

  return (
    <CollapsedPanel title="Form Fields">
      <Root>

        {/* ── Field Library ── */}
        <Section>
          <SectionHeader onClick={() => setLibraryOpen(v => !v)}>
            <SectionTitle>Add Field</SectionTitle>
            <SectionChevron $open={libraryOpen}>▾</SectionChevron>
          </SectionHeader>

          {libraryOpen && (
            <SectionBody>
              <SearchWrap>
                <SearchIcon>🔍</SearchIcon>
                <SearchInput
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search fields…"
                />
              </SearchWrap>

              {filteredCatalogue.map(cat => (
                <div key={cat.id}>
                  <CatLabel>{cat.label}</CatLabel>
                  {cat.fields.length === 0 && <NoResults>No fields found</NoResults>}
                  {cat.fields.map(field => (
                    <LibraryRow key={field.type} onClick={() => addField(field)}>
                      <LibIcon>{field.icon}</LibIcon>
                      <LibLabel>{field.label}</LibLabel>
                      <LibAdd>+ Add</LibAdd>
                    </LibraryRow>
                  ))}
                </div>
              ))}
            </SectionBody>
          )}
        </Section>

        {/* ── Form Structure ── */}
        <Section>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 16px' }}>
            <SectionTitle>
              Fields
              {questions.length > 0 && (
                <span style={{ marginLeft: 8, padding: '1px 7px', background: GRAY_100, borderRadius: 10, fontSize: '0.68rem', color: GRAY_500, fontWeight: 600 }}>
                  {questions.length}
                </span>
              )}
            </SectionTitle>
          </div>

          {questions.length === 0 ? (
            <EmptyState>
              <EmptyIcon>📋</EmptyIcon>
              <EmptyText>
                No fields yet.<br />Click a field above to add it.
              </EmptyText>
            </EmptyState>
          ) : (
            <StructureWrap>
              {questions.map((q, i) => (
                <FieldRow
                  key={i}
                  question={q}
                  index={i}
                  total={questions.length}
                  selected={selectedIdx === i}
                  onSelect={() => setSelectedIdx(idx => idx === i ? null : i)}
                  onChange={(updated) => updateQuestion(i, updated)}
                  onRemove={() => removeQuestion(i)}
                  onDuplicate={() => duplicateQuestion(i)}
                  onMoveUp={() => moveQuestion(i, -1)}
                  onMoveDown={() => moveQuestion(i, 1)}
                  isDragging={dragIndexRef.current === i}
                  isOver={overIndex === i}
                  onDragStart={onDragStart(i)}
                  onDragOver={onDragOver(i)}
                  onDrop={onDrop(i)}
                  onDragEnd={onDragEnd}
                  isPoll={settings.form_type === 'poll'}
                  isQuiz={settings.form_type === 'quiz'}
                />
              ))}
            </StructureWrap>
          )}
        </Section>

      </Root>
    </CollapsedPanel>
  );
}
